import React, { useState } from 'react';
import { ChevronRightIcon, ArrowLeftIcon, BookOpenIcon } from '@heroicons/react/24/outline';

interface DatabaseEngine {
  id: string;
  name: string;
  icon: string;
  description: string;
  tools: DatabaseTool[];
}

interface DatabaseTool {
  id: string;
  name: string;
  description: string;
  filename: string;
  popular?: boolean;
}

const databaseEngines: DatabaseEngine[] = [
  {
    id: 'sql-server',
    name: 'SQL Server',
    icon: '🗄️',
    description: 'Microsoft SQL Server export instructions',
    tools: [
      {
        id: 'ssms',
        name: 'SQL Server Management Studio',
        description: 'Export using SSMS Generate Scripts wizard',
        filename: 'sql-server-ssms.html',
        popular: true
      },
      {
        id: 'visual-studio',
        name: 'Visual Studio - SQL Server Data Tools',
        description: 'Create SQL project from existing database',
        filename: 'sql-server-visual-studio.html'
      },
      {
        id: 'vs-code',
        name: 'VS Code / Console',
        description: 'Using SQL Server extension and SQL Database Projects',
        filename: 'sql-server-vs-code.html'
      },
      {
        id: 'azure-data-studio',
        name: 'Azure Data Studio',
        description: 'Built-in SQL Server extension for database projects',
        filename: 'sql-server-azure-data-studio.html'
      },
      {
        id: 'source-repo',
        name: 'Source Code Repository',
        description: 'If you already have database objects in source control',
        filename: 'summary.html',
        popular: true
      }
    ]
  },
  {
    id: 'mysql',
    name: 'MySQL',
    icon: '🐬',
    description: 'MySQL database export instructions',
    tools: [
      {
        id: 'mysql-workbench',
        name: 'MySQL Workbench',
        description: 'Official MySQL GUI for database export',
        filename: 'mysql-workbench.html',
        popular: true
      },
      {
        id: 'dbeaver-mysql',
        name: 'DBeaver',
        description: 'Universal database tool for MySQL',
        filename: 'dbeaver.html'
      }
    ]
  },
  {
    id: 'oracle',
    name: 'Oracle Database',
    icon: '🔴',
    description: 'Oracle database export instructions',
    tools: [
      {
        id: 'sql-developer',
        name: 'SQL Developer',
        description: 'Oracle\'s official development environment',
        filename: 'oracle-sql-developer.html',
        popular: true
      }
    ]
  },
  {
    id: 'multi-db',
    name: 'Multi-Database Tools',
    icon: '🔧',
    description: 'Tools that support multiple database types',
    tools: [
      {
        id: 'dbeaver',
        name: 'DBeaver',
        description: 'Free universal database tool',
        filename: 'dbeaver.html',
        popular: true
      },
      {
        id: 'datagrip',
        name: 'DataGrip',
        description: 'JetBrains database IDE (30-day trial)',
        filename: 'datagrip.html'
      },
      {
        id: 'dbschema',
        name: 'DbSchema',
        description: 'Database designer and query tool',
        filename: 'dbschema.html'
      },
      {
        id: 'aws-sct',
        name: 'AWS Schema Conversion Tool',
        description: 'AWS tool for database migrations',
        filename: 'aws-sct.html'
      }
    ]
  }
];

export const DatabaseInstructionsMenu: React.FC = () => {
  const [selectedEngine, setSelectedEngine] = useState<DatabaseEngine | null>(null);
  const [hoveredEngine, setHoveredEngine] = useState<string | null>(null);
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);

  const handleToolClick = (tool: DatabaseTool) => {
    // Open instructions in a new authenticated window by constructing the URL from the current page
    const currentUrl = new URL(window.location.href);
    const instructionsUrl = new URL(`/${tool.filename}`, currentUrl.origin);
    
    // Preserve the token from the current URL
    if (currentUrl.searchParams.has('token')) {
      instructionsUrl.searchParams.set('token', currentUrl.searchParams.get('token')!);
    }
    
    window.open(instructionsUrl.toString(), '_blank', 'noopener,noreferrer');
  };

  const handleBackClick = () => {
    setSelectedEngine(null);
  };

  if (selectedEngine) {
    return (
      <div className="bg-black text-white shadow-lg rounded-xl p-8" style={{ border: '2px solid var(--caylent-light-green)' }}>
        <div className="mb-6">
          <button
            onClick={handleBackClick}
            className="flex items-center space-x-2 text-white hover:text-green-400 transition-colors duration-200 mb-4"
            style={{ fontSize: '14px' }}
            onMouseEnter={(e) => (e.target as HTMLButtonElement).style.color = 'var(--caylent-green)'}
            onMouseLeave={(e) => (e.target as HTMLButtonElement).style.color = 'white'}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Back to Database Selection</span>
          </button>
          
          <div className="flex items-center space-x-3 mb-2">
            <span className="text-2xl">{selectedEngine.icon}</span>
            <h2 className="caylent-large-title text-white" style={{ color: 'white' }}>
              {selectedEngine.name}
            </h2>
          </div>
          <p className="caylent-body text-white" style={{ fontSize: '14px', color: 'white', opacity: 0.8 }}>
            {selectedEngine.description}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {selectedEngine.tools.map((tool) => (
            <div
              key={tool.id}
              className="relative p-6 rounded-xl cursor-pointer transition-all duration-200 bg-black text-white"
              style={{ 
                border: hoveredTool === tool.id ? '2px solid var(--caylent-green)' : '2px solid var(--caylent-light-green)',
                backgroundColor: hoveredTool === tool.id ? 'rgba(151, 202, 111, 0.1)' : 'black'
              }}
              onMouseEnter={() => setHoveredTool(tool.id)}
              onMouseLeave={() => setHoveredTool(null)}
              onClick={() => handleToolClick(tool)}
            >
              {tool.popular && (
                <div 
                  className="absolute -top-2 -right-2 px-3 py-1 rounded-full text-xs font-medium"
                  style={{ 
                    backgroundColor: 'var(--caylent-green)',
                    color: 'white',
                    fontSize: '10px'
                  }}
                >
                  Popular
                </div>
              )}
              
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="caylent-medium-title text-white mb-2" style={{ color: 'white', fontSize: '16px' }}>
                    {tool.name}
                  </h3>
                  <p className="caylent-body text-white" style={{ fontSize: '12px', color: 'white', opacity: 0.8 }}>
                    {tool.description}
                  </p>
                </div>
                <ChevronRightIcon 
                  className="h-5 w-5 text-white ml-4 flex-shrink-0"
                  style={{ color: hoveredTool === tool.id ? 'var(--caylent-green)' : 'white' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white shadow-lg rounded-xl p-8" style={{ border: '2px solid var(--caylent-light-green)' }}>
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <BookOpenIcon className="h-8 w-8" style={{ color: 'var(--caylent-green)' }} />
          <h2 className="caylent-large-title text-white" style={{ color: 'white' }}>
            Database Export Instructions
          </h2>
        </div>
        <p className="caylent-body text-white" style={{ fontSize: '14px', color: 'white', opacity: 0.8 }}>
          Select your database engine to view specific export instructions for your environment.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {databaseEngines.map((engine) => (
          <div
            key={engine.id}
            className="p-6 rounded-xl cursor-pointer transition-all duration-200 bg-black text-white"
            style={{ 
              border: hoveredEngine === engine.id ? '2px solid var(--caylent-green)' : '2px solid var(--caylent-light-green)',
              backgroundColor: hoveredEngine === engine.id ? 'rgba(151, 202, 111, 0.1)' : 'black'
            }}
            onMouseEnter={() => setHoveredEngine(engine.id)}
            onMouseLeave={() => setHoveredEngine(null)}
            onClick={() => setSelectedEngine(engine)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-2xl">{engine.icon}</span>
                  <h3 className="caylent-medium-title text-white" style={{ color: 'white', fontSize: '20px' }}>
                    {engine.name}
                  </h3>
                </div>
                <p className="caylent-body text-white mb-4" style={{ fontSize: '12px', color: 'white', opacity: 0.8 }}>
                  {engine.description}
                </p>
                <div className="flex items-center space-x-2">
                  <span className="caylent-body-small text-white" style={{ fontSize: '10px', color: 'white', opacity: 0.6 }}>
                    {engine.tools.length} tool{engine.tools.length !== 1 ? 's' : ''} available
                  </span>
                  {engine.tools.some(tool => tool.popular) && (
                    <span 
                      className="px-2 py-1 rounded-full text-xs"
                      style={{ 
                        backgroundColor: 'var(--caylent-light-green)',
                        color: 'var(--caylent-dark-green)',
                        fontSize: '9px'
                      }}
                    >
                      Popular options
                    </span>
                  )}
                </div>
              </div>
              <ChevronRightIcon 
                className="h-6 w-6 text-white ml-4 flex-shrink-0"
                style={{ color: hoveredEngine === engine.id ? 'var(--caylent-green)' : 'white' }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 rounded-xl" style={{ backgroundColor: 'rgba(151, 202, 111, 0.1)', border: '1px solid var(--caylent-light-green)' }}>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 mt-0.5"
              style={{ color: 'var(--caylent-green)' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h4 className="caylent-small-title text-white mb-1" style={{ color: 'white', fontSize: '14px' }}>
              Need Help?
            </h4>
            <p className="caylent-body text-white" style={{ fontSize: '12px', color: 'white', opacity: 0.8 }}>
              Instructions will open in a new window. Each guide includes step-by-step screenshots and detailed explanations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};